import React, { useCallback, useEffect, useState } from 'react';
import { ImageIcon, Loader2, Sparkles, Store } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';

import Layout from '../components/Layout';
import MagasinBadge from '../components/MagasinBadge';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { stockPhotoUrl } from '../features/pos-v2/posV2PhotoUtils';

const canCarlosSuper = (user) => (
  user?.username === 'carlos'
  || user?.role === 'super_admin'
  || user?.magasin_role === 'super_admin'
);

const StockPhotoAiPage = () => {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [mag, setMag] = useState('M1');
  const [q, setQ] = useState('');
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState('');
  const [batchRunning, setBatchRunning] = useState(false);
  const [error, setError] = useState('');
  const [batchResult, setBatchResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/stock/photos-ai/hub', {
        params: { magasin_id: mag, missing_only: true, q: q.trim() || undefined, limit: 100 },
      });
      setHub(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || t('stockPhotoAi.errorLoad'));
      setHub(null);
    } finally {
      setLoading(false);
    }
  }, [api, mag, q]);

  useEffect(() => {
    if (!canCarlosSuper(user)) return;
    load();
  }, [load, user]);

  if (!canCarlosSuper(user)) {
    return <Navigate to="/dashboard" replace />;
  }

  const generateOne = async (reference) => {
    setGenerating(reference);
    setError('');
    try {
      await api.post('/stock/photos-ai/generate', { magasin_id: mag, reference, force: false });
      await load();
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('stockPhotoAi.errorGenerate'));
    } finally {
      setGenerating('');
    }
  };

  const generateBatch = async () => {
    const missing = hub?.stats?.missing || 0;
    if (!missing) return;
    const max = Math.min(missing, 10);
    if (!window.confirm(t('stockPhotoAi.confirmBatch', { max, mag }))) return;
    setBatchRunning(true);
    setError('');
    setBatchResult(null);
    try {
      const res = await api.post('/stock/photos-ai/generate-batch', {
        magasin_id: mag,
        max_items: max,
        force: false,
      });
      setBatchResult(res.data);
      await load();
    } catch (err) {
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : t('stockPhotoAi.errorBatch'));
    } finally {
      setBatchRunning(false);
    }
  };

  const stats = hub?.stats || { total: 0, with_photo: 0, missing: 0 };
  const items = hub?.items || [];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6" data-testid="stock-photos-ai-page">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Sparkles className="text-amber-400" size={26} />
              {t('stockPhotoAi.title')}
            </h1>
            <p className="text-sm text-gray-400 mt-1">
              {t('stockPhotoAi.subtitle')}
            </p>
          </div>
          <Link to="/admin/cles-api" className="text-xs text-red-400 underline">{t('stockPhotoAi.apiKeysLink')}</Link>
        </header>

        <div className="flex flex-wrap gap-2">
          {['M1', 'M2'].map((code) => (
            <button
              key={code}
              type="button"
              onClick={() => setMag(code)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl border font-bold min-h-[44px] ${
                mag === code ? 'border-red-500/50 bg-red-950/30 text-white' : 'border-white/10 text-gray-400'
              }`}
            >
              <Store size={16} /> {code}
            </button>
          ))}
          <MagasinBadge code={mag} size="md" />
        </div>

        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl border border-white/10 bg-[#151820] p-4">
            <div className="text-2xl font-black text-white">{stats.total}</div>
            <div className="text-xs text-gray-500">{t('stockPhotoAi.statsTotal', { mag })}</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#151820] p-4">
            <div className="text-2xl font-black text-emerald-400">{stats.with_photo}</div>
            <div className="text-xs text-gray-500">{t('stockPhotoAi.statsWithPhoto')}</div>
          </div>
          <div className="rounded-xl border border-amber-500/30 bg-amber-950/20 p-4">
            <div className="text-2xl font-black text-amber-300">{stats.missing}</div>
            <div className="text-xs text-gray-500">{t('stockPhotoAi.statsMissing')}</div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            className="flex-1 min-w-[200px] px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white text-sm"
            placeholder={t('stockPhotoAi.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <button type="button" onClick={load} className="px-4 py-2 rounded-lg bg-white/10 text-sm font-semibold">{t('stockPhotoAi.filter')}</button>
          <button
            type="button"
            disabled={batchRunning || !stats.missing}
            onClick={generateBatch}
            className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 font-semibold text-sm disabled:opacity-40 flex items-center gap-2"
          >
            {batchRunning ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} />}
            {t('stockPhotoAi.generateBatch')}
          </button>
        </div>

        {error && (
          <p className="text-sm text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
        )}
        {batchResult && (
          <p className="text-sm text-emerald-300 bg-emerald-950/20 border border-emerald-500/30 rounded-lg px-3 py-2">
            {t('stockPhotoAi.batchResult', {
              mag,
              ok: batchResult.ok,
              skipped: batchResult.skipped,
              errors: batchResult.errors,
            })}
          </p>
        )}

        {loading ? (
          <p className="text-center text-gray-500 py-12 flex justify-center gap-2">
            <Loader2 className="animate-spin" /> {t('stockPhotoAi.loading')}
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((item) => {
              const thumb = stockPhotoUrl(item.thumbnail_url);
              return (
                <article key={item.id || item.reference} className="rounded-xl border border-white/10 bg-[#151820] p-3 flex gap-3">
                  <div className="w-16 h-16 rounded-lg bg-black/40 border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className="text-gray-600" size={24} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-mono text-gray-500 truncate">{item.reference}</p>
                    <p className="text-sm font-semibold text-white line-clamp-2">{item.designation}</p>
                    <p className="text-[10px] text-gray-500 mt-1">{t('stockPhotoAi.stockLabel', { qty: item.quantite })}</p>
                    <button
                      type="button"
                      disabled={generating === item.reference || batchRunning}
                      onClick={() => generateOne(item.reference)}
                      className="mt-2 text-xs px-2 py-1 rounded bg-amber-950/40 border border-amber-500/30 text-amber-200 flex items-center gap-1"
                    >
                      {generating === item.reference ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                      {t('stockPhotoAi.generateOne')}
                    </button>
                  </div>
                </article>
              );
            })}
            {!items.length && (
              <p className="col-span-full text-center text-gray-500 py-8">{t('stockPhotoAi.empty', { mag })}</p>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StockPhotoAiPage;
